import { get, omit, set } from "lodash-es";
import { computed, ComputedRef, Reactive, reactive, Ref, ref, unref } from "vue";
import { Schema, ValidationError } from "yup";

export type FieldPath = string;
export type ErrorMessages = Record<FieldPath, string>;

export interface ValidatorOptions {
  /** 유효성 검사가 실패할 때 즉시 중단할지 여부 */
  abortEarly?: boolean;
}

export interface ValidatorResult {
  /** 비동기적으로 데이터 유효성 검사 수행 */
  validate: <T>(data: T, options?: ValidatorOptions) => Promise<boolean>;

  /** 동기적으로 데이터 유효성 검사 수행 */
  validateSync: <T>(data: T, options?: ValidatorOptions) => boolean;

  /** 특정 필드만 유효성 검사 수행 */
  validateField: <T>(data: T, field: FieldPath) => Promise<boolean>;

  /** 모든 에러 초기화 */
  reset: () => void;

  /** 에러 존재 여부 */
  hasError: ComputedRef<boolean>;

  /** 현재 에러 상태 가져오기 */
  getErrors: () => ErrorMessages;

  /** 특정 필드의 에러 메시지 가져오기 */
  getFieldError: (field: FieldPath) => string | null;

  /** 특정 필드의 에러 제거 */
  clearFieldError: (field: FieldPath) => void;

  /** 특정 필드에 에러 설정 */
  setFieldError: (field: FieldPath, message: string) => void;

  onError?: (callback: (error: ErrorMessages) => void) => void;

  onValid?: (callback: () => void) => void;
}

/**
 * Yup 스키마를 사용한 유효성 검사 유틸리티
 * @param schema Yup 스키마
 * @returns ValidatorResult 객체
 */
export const useValidator = (schema: Schema<any>): Reactive<ValidatorResult> => {
  if (!schema) {
    throw new Error("The schema must be a Yup schema.");
  }

  const errors: Ref<ErrorMessages> = ref({});

  /**
   * 기본 유효성 검사 옵션
   */
  const defaultOptions: ValidatorOptions = {
    abortEarly: false,
  };

  /**
   * 비동기적으로 데이터 유효성 검사 수행
   * @param data 검증할 데이터
   * @param options 유효성 검사 옵션
   * @returns 유효성 검사 통과 여부
   */
  const validate = async <T>(data: T, options: ValidatorOptions = {}): Promise<boolean> => {
    try {
      await schema.validate(data, { ...defaultOptions, ...options });
      if (!options.abortEarly) {
        errors.value = {};
      }
      return true;
    } catch (error) {
      if (error instanceof ValidationError) {
        processValidationErrors(error);
      }
      return false;
    }
  };

  /**
   * 동기적으로 데이터 유효성 검사 수행
   * @param data 검증할 데이터
   * @param options 유효성 검사 옵션
   * @returns 유효성 검사 통과 여부
   */
  const validateSync = <T>(data: T, options: ValidatorOptions = {}): boolean => {
    try {
      schema.validateSync(data, { ...defaultOptions, ...options });
      if (!options.abortEarly) {
        errors.value = {};
      }
      return true;
    } catch (error) {
      if (error instanceof ValidationError) {
        processValidationErrors(error);
      }
      return false;
    }
  };

  /**
   * 특정 필드만 유효성 검사 수행
   * @param data 전체 데이터
   * @param field 검사할 필드 경로
   * @returns 유효성 검사 통과 여부
   */
  const validateField = async <T>(data: T, field: FieldPath): Promise<boolean> => {
    try {
      // 필드 값 추출
      const fieldValue = get(data, field);

      // 필드가 중첩된 경우를 위한 임시 객체 생성
      const testObj = {};
      set(testObj, field, fieldValue);

      // 해당 필드만 검증 수행
      await schema.validateAt(field, testObj, { abortEarly: true });

      // 성공 시 해당 필드 에러 제거
      clearFieldError(field);
      return true;
    } catch (error) {
      if (error instanceof ValidationError) {
        // 해당 필드 에러 설정
        setFieldError(field, error.message);
      }
      return false;
    }
  };

  /**
   * ValidationError 객체를 처리하여 에러 상태 업데이트
   * @param error ValidationError 객체
   */
  const processValidationErrors = (error: ValidationError): void => {
    const newErrors: ErrorMessages = {};

    error.inner.forEach((e) => {
      if (e.path) {
        set(newErrors, e.path, e.message);

        // 배열 필드 처리
        if (e.path.includes("[")) {
          const baseField = e.path.split("[")[0];

          if (!get(newErrors, baseField)) {
            set(newErrors, baseField, e.message);
          }
        }
      }
    });

    // 한 번에 상태 업데이트 (반응성 최적화)
    errors.value = newErrors;
  };

  /**
   * 모든 에러 초기화
   */
  const reset = (): void => {
    errors.value = {};
  };

  /**
   * 특정 필드의 에러 메시지 반환 (배열 처리 포함)
   * @param field 필드명
   * @returns 에러 메시지 또는 null
   */
  const getFieldError = (field: FieldPath): string | null => {
    const directError = get(unref(errors), field);

    if (directError) {
      return directError;
    }

    if (!field.includes("[")) {
      const errorObj = unref(errors);
      const arrayErrorKeys = Object.keys(errorObj).filter((key) => key.startsWith(`${field}[`) || key === field);

      if (arrayErrorKeys.length > 0) {
        return get(errorObj, arrayErrorKeys[0]);
      }
    }

    return null;
  };

  /**
   * 특정 필드의 에러 제거 (배열 처리 포함)
   * @param field 필드명
   */
  const clearFieldError = (field: FieldPath): void => {
    // 1. 직접 매칭되는 필드 제거
    if (get(errors.value, field)) {
      errors.value = omit(errors.value, [field]) as ErrorMessages;
    }

    // 2. 배열 필드 처리
    if (!field.includes("[")) {
      const newErrors = { ...errors.value };
      const keysToRemove: string[] = [];

      Object.keys(newErrors).forEach((key) => {
        if (key === field || key.startsWith(`${field}[`)) {
          keysToRemove.push(key);
        }
      });

      errors.value = omit(newErrors, keysToRemove) as ErrorMessages;
    }
  };

  /**
   * 특정 필드에 에러 메시지 설정
   * @param field 필드명
   * @param message 에러 메시지
   */
  const setFieldError = (field: FieldPath, message: string): void => {
    errors.value = {
      ...errors.value,
      [field]: message,
    };
  };

  /**
   * 모든 에러 메시지 반환
   */
  const getErrors = (): ErrorMessages => {
    return unref(errors);
  };

  /**
   * 에러 존재 여부
   */
  const hasError: ComputedRef<boolean> = computed(() => Object.keys(errors.value).length > 0);

  const onError = (callback: (error: ErrorMessages) => void) => callback(getErrors());

  const onValid = (callback: () => void) => callback();

  return reactive({
    validate,
    validateSync,
    validateField,
    reset,
    hasError,
    getErrors,
    getFieldError,
    clearFieldError,
    setFieldError,
    onError,
    onValid,
  });
};
